import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Brain pipeline states
 */
export type BrainPipelineState =
  | 'idle'
  | 'connecting'
  | 'streaming'
  | 'processing'
  | 'complete'
  | 'error';

/**
 * Individual brain status
 */
export type BrainStatus = 'idle' | 'processing' | 'complete' | 'error';

export interface BrainProgress {
  brain: string;
  progress: number;
  status: string;
  details?: Record<string, unknown>;
}

export interface PartialResult {
  resultType: string;
  data: Record<string, unknown>;
  requiresInput: boolean;
  timeoutMs?: number;
}

export interface ArtistMatch {
  artistName: string;
  similarityScore: number;
  reasoning: string;
  visualStyleKeywords: string[];
}

export interface ProductionBrief {
  projectId: string;
  brief: Record<string, unknown>;
  totalProcessingTimeMs: number;
}

interface BrainStatuses {
  musicAnalyst: BrainStatus;
  visualDesigner: BrainStatus;
  screenwriter: BrainStatus;
}

interface UseBrainPipelineResult {
  // State
  state: BrainPipelineState;
  brainStatuses: BrainStatuses;
  currentBrain: string | null;
  progress: number;
  error: string | null;

  // Partial results
  artistMatches: ArtistMatch[];
  visualStylePreview: Record<string, unknown> | null;
  scenePreview: Record<string, unknown> | null;

  // Final result
  productionBrief: ProductionBrief | null;

  // Timing
  elapsedSeconds: number;

  // Actions
  connect: (projectId: string) => Promise<void>;
  disconnect: () => void;
  sendUserSelection: (selectionType: string, selection: Record<string, unknown>) => void;
  reset: () => void;
}

const BRAIN_WS_URL = import.meta.env.VITE_BRAIN_WS_URL || 'wss://jelly-bop-brain-production.up.railway.app';

/**
 * Hook for connecting to jelly-bop-brain WebSocket and receiving real-time updates.
 *
 * Usage:
 * ```tsx
 * const { state, brainStatuses, progress, artistMatches, productionBrief, connect } = useBrainPipeline();
 *
 * // Connect when video generation starts
 * useEffect(() => {
 *   if (projectId && shouldUseBrain) {
 *     connect(projectId);
 *   }
 * }, [projectId]);
 *
 * // Render progress
 * return (
 *   <BrainPipelineProgress
 *     state={state}
 *     brainStatuses={brainStatuses}
 *     progress={progress}
 *     artistMatches={artistMatches}
 *   />
 * );
 * ```
 */
export function useBrainPipeline(): UseBrainPipelineResult {
  const [state, setState] = useState<BrainPipelineState>('idle');
  const [brainStatuses, setBrainStatuses] = useState<BrainStatuses>({
    musicAnalyst: 'idle',
    visualDesigner: 'idle',
    screenwriter: 'idle',
  });
  const [currentBrain, setCurrentBrain] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Partial results
  const [artistMatches, setArtistMatches] = useState<ArtistMatch[]>([]);
  const [visualStylePreview, setVisualStylePreview] = useState<Record<string, unknown> | null>(null);
  const [scenePreview, setScenePreview] = useState<Record<string, unknown> | null>(null);

  // Final result
  const [productionBrief, setProductionBrief] = useState<ProductionBrief | null>(null);

  // Timing
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const projectIdRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Timer for elapsed time
  useEffect(() => {
    if (state === 'streaming' || state === 'processing') {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }

      timerRef.current = window.setInterval(() => {
        if (startTimeRef.current) {
          setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [state]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      const msgType = data.type;

      console.log('[BrainPipeline] Received:', msgType, data);

      switch (msgType) {
        case 'CONNECTION_ACK':
          console.log('[BrainPipeline] Connected, session:', data.session_id);
          setState('streaming');
          if (data.brain_status) {
            setBrainStatuses({
              musicAnalyst: data.brain_status.music_analyst || 'idle',
              visualDesigner: data.brain_status.visual_designer || 'idle',
              screenwriter: data.brain_status.screenwriter || 'idle',
            });
          }
          break;

        case 'STAGE_RECEIVED':
          console.log('[BrainPipeline] Stage received:', data.stage);
          break;

        case 'BRAIN_PROGRESS': {
          const brain = data.brain as string;
          const progressPct = data.progress as number;
          const status = data.status as string;

          setCurrentBrain(brain);
          setProgress(progressPct);
          setState('processing');

          // Update brain status
          setBrainStatuses(prev => ({
            ...prev,
            [brain === 'music_analyst' ? 'musicAnalyst' :
             brain === 'visual_designer' ? 'visualDesigner' :
             'screenwriter']: 'processing',
          }));

          console.log(`[BrainPipeline] ${brain}: ${progressPct}% - ${status}`);
          break;
        }

        case 'BRAIN_COMPLETE': {
          const brain = data.brain as string;
          console.log(`[BrainPipeline] ${brain} complete in ${data.processing_time_ms}ms`);

          // Update brain status
          setBrainStatuses(prev => ({
            ...prev,
            [brain === 'music_analyst' ? 'musicAnalyst' :
             brain === 'visual_designer' ? 'visualDesigner' :
             'screenwriter']: 'complete',
          }));
          break;
        }

        case 'PARTIAL_RESULT': {
          const resultType = data.result_type as string;
          const resultData = data.data as Record<string, unknown>;

          console.log('[BrainPipeline] Partial result:', resultType);

          if (resultType === 'artist_matches') {
            const matches = (resultData.matches || []) as Array<{
              artist_name: string;
              similarity_score: number;
              reasoning: string;
              visual_style_keywords: string[];
            }>;
            setArtistMatches(matches.map(m => ({
              artistName: m.artist_name,
              similarityScore: m.similarity_score,
              reasoning: m.reasoning,
              visualStyleKeywords: m.visual_style_keywords,
            })));
          } else if (resultType === 'visual_style_preview') {
            setVisualStylePreview(resultData);
          } else if (resultType === 'scene_preview') {
            setScenePreview(resultData);
          }
          break;
        }

        case 'REQUEST_INPUT':
          console.log('[BrainPipeline] Input requested:', data.input_type);
          // Could emit an event or call a callback here
          break;

        case 'PRODUCTION_BRIEF':
          console.log('[BrainPipeline] Production brief received!');
          setProductionBrief({
            projectId: data.project_id,
            brief: data.brief,
            totalProcessingTimeMs: data.total_processing_time_ms,
          });
          setState('complete');
          break;

        case 'ERROR':
          console.error('[BrainPipeline] Error:', data.message);
          setError(data.message);
          if (!data.recoverable) {
            setState('error');
          }
          break;

        default:
          console.log('[BrainPipeline] Unknown message type:', msgType);
      }
    } catch (err) {
      console.error('[BrainPipeline] Failed to parse message:', err);
    }
  }, []);

  // Connect to brain WebSocket
  const connect = useCallback(async (projectId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[BrainPipeline] Already connected');
      return;
    }

    projectIdRef.current = projectId;
    setState('connecting');
    setError(null);
    startTimeRef.current = null;
    setElapsedSeconds(0);

    const url = `${BRAIN_WS_URL}/ws/pipeline/${projectId}?role=frontend`;
    console.log('[BrainPipeline] Connecting to:', url);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[BrainPipeline] WebSocket connected');
      };

      ws.onmessage = handleMessage;

      ws.onerror = (event) => {
        console.error('[BrainPipeline] WebSocket error:', event);
        setError('Connection error');
        setState('error');
      };

      ws.onclose = (event) => {
        console.log('[BrainPipeline] WebSocket closed:', event.code, event.reason);
        if (state !== 'complete' && state !== 'error') {
          // Unexpected close
          if (event.code !== 1000) {
            setError(`Connection closed: ${event.reason || 'Unknown reason'}`);
            setState('error');
          }
        }
      };
    } catch (err) {
      console.error('[BrainPipeline] Failed to connect:', err);
      setError(err instanceof Error ? err.message : 'Connection failed');
      setState('error');
    }
  }, [handleMessage, state]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
  }, []);

  // Send user selection
  const sendUserSelection = useCallback((selectionType: string, selection: Record<string, unknown>) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[BrainPipeline] Cannot send - not connected');
      return;
    }

    const message = {
      type: 'USER_SELECTION',
      project_id: projectIdRef.current,
      selection_type: selectionType,
      selection,
      timestamp: new Date().toISOString(),
    };

    wsRef.current.send(JSON.stringify(message));
    console.log('[BrainPipeline] Sent user selection:', selectionType);
  }, []);

  // Reset state
  const reset = useCallback(() => {
    disconnect();
    setState('idle');
    setBrainStatuses({
      musicAnalyst: 'idle',
      visualDesigner: 'idle',
      screenwriter: 'idle',
    });
    setCurrentBrain(null);
    setProgress(0);
    setError(null);
    setArtistMatches([]);
    setVisualStylePreview(null);
    setScenePreview(null);
    setProductionBrief(null);
    setElapsedSeconds(0);
    startTimeRef.current = null;
    projectIdRef.current = null;
  }, [disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    state,
    brainStatuses,
    currentBrain,
    progress,
    error,
    artistMatches,
    visualStylePreview,
    scenePreview,
    productionBrief,
    elapsedSeconds,
    connect,
    disconnect,
    sendUserSelection,
    reset,
  };
}
